import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, CheckCircle, AlertCircle, Loader2, 
  BookOpen, GraduationCap, Brain, Target, X 
} from 'lucide-react';
import { client } from '../api/client';

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'parsing' | 'generating' | 'completed' | 'error';
  progress: number;
  message: string;
  result?: {
    topicId?: number;
    questionsGenerated?: number;
    knowledgeNodesCreated?: number;
    studyProgramId?: number;
  };
}

const GRADES = [
  { value: 7, label: 'Year 7' },
  { value: 8, label: 'Year 8' },
  { value: 9, label: 'Year 9' },
];

const SUBJECTS = [
  { value: 1, label: 'Mathematics / 数学' },
  { value: 2, label: 'English / 英语' },
  { value: 3, label: 'Science / 科学' },
  { value: 4, label: 'Physics / 物理' },
  { value: 5, label: 'Chemistry / 化学' },
];

export function MaterialImportPage() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<number>(7);
  const [selectedSubject, setSelectedSubject] = useState<number>(1);
  const [topicName, setTopicName] = useState('');
  const [questionsPerTopic, setQuestionsPerTopic] = useState(30);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      f => f.type === 'application/pdf'
    );
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []).filter(
      f => f.type === 'application/pdf'
    );
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles: File[]) => {
    const uploadFiles: UploadFile[] = newFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending',
      progress: 0,
      message: '等待处理',
    }));
    setFiles(prev => [...prev, ...uploadFiles]);
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const processFiles = async () => {
    setIsProcessing(true);

    for (const uploadFile of files) {
      if (uploadFile.status !== 'pending') continue;

      try {
        updateFile(uploadFile.id, { 
          status: 'uploading', 
          progress: 10, 
          message: '上传文件...' 
        });

        const formData = new FormData();
        formData.append('file', uploadFile.file);
        formData.append('grade', selectedGrade.toString());
        formData.append('subject_id', selectedSubject.toString());
        formData.append('topic_name', topicName || uploadFile.file.name.replace('.pdf', ''));

        const uploadRes = await client.api.fetch('/api/materials/upload', {
          method: 'POST',
          body: formData,
        });

        if (!uploadRes.ok) throw new Error('上传失败');
        const uploadData = await uploadRes.json();

        updateFile(uploadFile.id, { 
          status: 'parsing', 
          progress: 30, 
          message: '解析 PDF 内容...' 
        });

        const parseRes = await client.api.fetch('/api/materials/parse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            materialId: uploadData.data.id 
          }),
        });

        if (!parseRes.ok) throw new Error('解析失败');
        const parseData = await parseRes.json();

        updateFile(uploadFile.id, { 
          status: 'generating', 
          progress: 50, 
          message: '生成题目和学习程序...' 
        });

        // Parallel: generate questions AND study program
        const [questionsResult, studyProgramResult] = await Promise.allSettled([
          client.api.fetch('/api/materials/generate-questions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              materialId: uploadData.data.id,
              questionCount: questionsPerTopic,
              questionTypes: ['multiple_choice', 'fill_blank', 'short_answer'],
            }),
          }).then(res => res.json()),
          client.api.fetch('/api/materials/generate-study-program', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              materialId: uploadData.data.id,
            }),
          }).then(res => res.json())
        ]);

        const questionsData = questionsResult.status === 'fulfilled' ? questionsResult.value : null;
        const studyProgramData = studyProgramResult.status === 'fulfilled' ? studyProgramResult.value : null;

        updateFile(uploadFile.id, { 
          status: 'completed', 
          progress: 100, 
          message: '处理完成！',
          result: {
            topicId: uploadData.data.topicId,
            questionsGenerated: questionsData?.data?.questionsGenerated || questionsPerTopic,
            knowledgeNodesCreated: parseData.data?.nodesCreated || 0,
            studyProgramId: studyProgramData?.studyProgramId,
          }
        });

      } catch (error) {
        updateFile(uploadFile.id, { 
          status: 'error', 
          progress: 0, 
          message: (error as Error).message 
        });
      }
    }

    setIsProcessing(false);
  };

  const completedCount = files.filter(f => f.status === 'completed').length;
  const pendingCount = files.filter(f => f.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">教学资料导入</h1>
          <p className="text-slate-600 max-w-lg mx-auto">
            上传 PDF 教学资料，系统将自动解析内容、提取知识点并生成练习题
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-500" />
            选择资料信息
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">年级</label>
              <select
                value={selectedGrade}
                onChange={e => setSelectedGrade(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {GRADES.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">学科</label>
              <select
                value={selectedSubject}
                onChange={e => setSelectedSubject(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {SUBJECTS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">主题名称 (可选)</label>
              <input
                type="text"
                value={topicName}
                onChange={e => setTopicName(e.target.value)}
                placeholder="如：Linear Equations"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              每个资料生成题目数量: {questionsPerTopic}
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={5}
              value={questionsPerTopic}
              onChange={e => setQuestionsPerTopic(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>10 题</span>
              <span>100 题</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
          >
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700 mb-1">
                拖拽 PDF 文件到此处
              </p>
              <p className="text-sm text-slate-500">
                或点击选择文件 (支持多选)
              </p>
            </label>
          </div>
        </motion.div>

        <AnimatePresence>
          {files.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900">
                  文件列表 ({files.length} 个文件)
                </h2>
                {pendingCount > 0 && (
                  <button
                    onClick={processFiles}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        处理中...
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4" />
                        开始处理 ({pendingCount} 个待处理)
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {files.map(file => (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                  >
                    <FileText className="w-8 h-8 text-slate-400 flex-shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {file.file.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      
                      {file.status !== 'pending' && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  file.status === 'completed' ? 'bg-emerald-500' :
                                  file.status === 'error' ? 'bg-red-500' :
                                  'bg-blue-500'
                                }`}
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 w-10">
                              {file.progress}%
                            </span>
                          </div>
                          <p className="text-xs text-slate-600">{file.message}</p>
                        </div>
                      )}
                    </div>

                    {file.status === 'completed' && file.result && (
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-emerald-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          完成
                        </div>
                        <p className="text-xs text-slate-500">
                          {file.result.questionsGenerated} 题目
                        </p>
                      </div>
                    )}

                    {file.status === 'error' && (
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-red-600 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          失败
                        </div>
                      </div>
                    )}

                    {file.status === 'pending' && (
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-1 text-slate-400 hover:text-red-500"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {completedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6"
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Brain className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900 mb-1">
                  导入完成！
                </h3>
                <p className="text-emerald-700 text-sm mb-3">
                  已成功处理 {completedCount} 个文件。你现在可以：
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="/subjects"
                    className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                  >
                    查看题库
                  </a>
                  <a
                    href="/materials"
                    className="px-3 py-1.5 bg-white text-emerald-700 text-sm rounded-lg border border-emerald-300 hover:bg-emerald-50"
                  >
                    查看学习程序
                  </a>
                  <a
                    href="/knowledge-graph"
                    className="px-3 py-1.5 bg-white text-emerald-700 text-sm rounded-lg border border-emerald-300 hover:bg-emerald-50"
                  >
                    查看知识图谱
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
